import React, { useState, useEffect, useCallback, useRef } from 'react';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import moment from 'moment';

import { SkeletonTable, SkeletonLines } from '../SpotlightsSkeleton';
import TrialGrantModal from '../TrialGrantModal';
import SupplementalInvoiceModal from '../SupplementalInvoiceModal';
import ScheduledInvoiceModal from '../ScheduledInvoiceModal';
import BillingCreditModal from '../BillingCreditModal';
import SpotlightInvoiceExemption from '../SpotlightInvoiceExemption';
import useCollapse from '../useCollapse';
import './SpotlightsBilling.scss';
import {
  getBilling,
  getBrandInvoices,
  getBrandPaymentMethods,
  setBrandPaymentMethod,
  setSpotlightNetTerms,
  setSpotlightSkipPaymentMethod,
  getBillingCredits,
  cancelBillingCredit,
  setInvoiceGrouping,
  getTrials,
  cancelTrial,
  editTrial
} from '../../../api/spotlights';
import { fmtMoney, fmtMoney2, fmtInt } from '../../../helpers/spotlights_dashboard_helpers';
import { getSmartImage } from '../../../helpers/helpers';

const SUBTABS = [
  { key: 'billing', label: 'Billing' },
  { key: 'trials', label: 'Trials' }
];
const FILTERS = [
  { key: '', label: 'All' },
  { key: 'unpaid', label: 'Unpaid invoices' },
  { key: 'billingPaused', label: 'Billing paused' },
  { key: 'noPaymentMethod', label: 'No payment method' },
  { key: 'notOnboarded', label: 'Not onboarded' }
];

const SpotlightsBilling = () => {
  const [subtab, setSubtab] = useState('billing');
  return (
    <div className='spotlights-billing'>
      <div className='sl-controls'>
        <div className='sl-segmented'>
          {SUBTABS.map(s => (
            <div key={s.key} className={cn('sl-segment', { active: subtab === s.key })} onClick={() => setSubtab(s.key)}>
              {s.label}
            </div>
          ))}
        </div>
      </div>
      {subtab === 'billing' ? <BillingTable /> : <TrialsTable />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Billing table
// ---------------------------------------------------------------------------
const BillingTable = () => {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getBilling({ query: search, filter });
      setRows(r.brands || []);
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to load billing.');
    } finally {
      setLoading(false);
    }
  }, [search, filter]);

  useEffect(() => {
    load();
  }, [load]);

  const submitSearch = e => {
    e.preventDefault();
    setSearch(query);
  };

  return (
    <>
      <div className='sl-controls'>
        <form onSubmit={submitSearch}>
          <input className='sl-search' type='text' placeholder='Search brands…' value={query} onChange={e => setQuery(e.target.value)} />
        </form>
        <div className='sl-segmented'>
          {FILTERS.map(f => (
            <div key={f.key} className={cn('sl-segment', { active: filter === f.key })} onClick={() => setFilter(f.key)}>
              {f.label}
            </div>
          ))}
        </div>
      </div>

      {error ? (
        <div className='sl-error'>{error}</div>
      ) : loading && !rows.length ? (
        <SkeletonTable rows={8} cols={8} />
      ) : (
        <div className={cn('sl-refreshable', { 'sl-refreshing': loading })}>
          <table className='sl-table'>
            <thead>
              <tr>
                <th className='no-sort' style={{ width: 24 }} />
                <th className='no-sort'>Brand</th>
                <th className='no-sort'>Payment method</th>
                <th className='no-sort'>Cadence</th>
                <th className='no-sort num'>Fee %</th>
                <th className='no-sort num'>Unpaid</th>
                <th className='no-sort num'>Paid total</th>
                <th className='no-sort'>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(b => (
                <BillingRow
                  key={b.Brand_id}
                  brand={b}
                  expanded={expanded === b.Brand_id}
                  onToggle={() => setExpanded(expanded === b.Brand_id ? null : b.Brand_id)}
                  onRefresh={load}
                  onBrandPatch={(brandId, patch) => setRows(prev => prev.map(row => (row.Brand_id === brandId ? { ...row, ...patch } : row)))}
                />
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={8} className='sl-empty'>
                    No brands match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

const BillingRow = ({ brand, expanded, onToggle, onBrandPatch, onRefresh }) => {
  const { mounted, closing } = useCollapse(expanded);
  return (
    <>
      <tr className='sl-row-clickable' onClick={onToggle}>
        <td>
          <span className={cn('sl-caret', { open: expanded })}>▸</span>
        </td>
        <td>
          <div className='sl-brand-cell'>
            {brand.brandLogo && <img src={getSmartImage(brand.brandLogo)} alt='' />}
            {brand.brandName}
            {!brand.onboarded && <span className='sl-pill amber'>Not onboarded</span>}
          </div>
        </td>
        <td>
          {brand.hasPaymentMethod ? (
            <span className='sl-pill green'>On file</span>
          ) : (
            !brand.skipPaymentMethod && <span className='sl-pill red'>Missing</span>
          )}
        </td>
        <td>{brand.billingCadence || '—'}</td>
        <td className='num'>{brand.platformFeePercentage != null ? `${brand.platformFeePercentage}%` : '—'}</td>
        <td className='num'>{brand.unpaidCount > 0 ? `${fmtInt(brand.unpaidCount)} · ${fmtMoney(brand.unpaidAmount)}` : '—'}</td>
        <td className='num'>{fmtMoney(brand.paidAmount)}</td>
        <td>
          <div className='sl-pill-row'>
            {brand.invoiceExempt && <span className='sl-pill blue'>Invoice exempt</span>}
            {brand.billingPaused && <span className='sl-pill amber'>Paused</span>}
            {brand.netPaymentTerms > 0 && <span className='sl-pill blue'>Net {brand.netPaymentTerms}d</span>}
            {brand.skipPaymentMethod && <span className='sl-pill blue'>Manual pay</span>}
          </div>
        </td>
      </tr>
      {mounted && (
        <tr>
          <td colSpan={8} className={cn('sl-expanded', { closing })}>
            <BillingDetail brand={brand} onBrandPatch={onBrandPatch} onRefresh={onRefresh} />
          </td>
        </tr>
      )}
    </>
  );
};

const BillingDetail = ({ brand, onBrandPatch, onRefresh }) => {
  const [invoices, setInvoices] = useState(null);
  const [pm, setPm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPm, setSelectedPm] = useState('');
  const [saving, setSaving] = useState(false);
  const [netTerms, setNetTerms] = useState(brand.netPaymentTerms || 0);
  const [netTermsSaved, setNetTermsSaved] = useState(brand.netPaymentTerms || 0);
  const [savingNetTerms, setSavingNetTerms] = useState(false);
  const [skipPm, setSkipPm] = useState(!!brand.skipPaymentMethod);
  const [savingSkipPm, setSavingSkipPm] = useState(false);
  const [supplementalOpen, setSupplementalOpen] = useState(false);
  const [scheduledOpen, setScheduledOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [credits, setCredits] = useState(null);
  const [grouping, setGrouping] = useState(brand.invoiceGrouping || 'combined');
  const [savingGrouping, setSavingGrouping] = useState(false);

  const loadPm = useCallback(() => {
    getBrandPaymentMethods(brand.Brand_id)
      .then(r => {
        setPm(r);
        setSelectedPm(r.currentPaymentMethodId || '');
      })
      .catch(() => setPm(false));
  }, [brand.Brand_id]);

  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const loadInvoices = useCallback(() => {
    return getBrandInvoices(brand.Brand_id)
      .then(r => mountedRef.current && setInvoices(r.invoices))
      .catch(() => mountedRef.current && setInvoices([]));
  }, [brand.Brand_id]);

  const loadCredits = useCallback(() => {
    return getBillingCredits(brand.Brand_id)
      .then(r => mountedRef.current && setCredits(r.credits))
      .catch(() => mountedRef.current && setCredits([]));
  }, [brand.Brand_id]);

  useEffect(() => {
    loadInvoices().finally(() => mountedRef.current && setLoading(false));
    loadPm();
    loadCredits();
  }, [loadInvoices, loadPm, loadCredits]);

  const savePm = async () => {
    if (!selectedPm) return;
    setSaving(true);
    try {
      await setBrandPaymentMethod(brand.Brand_id, selectedPm);
      cogoToast.success('Payment method updated.');
      loadPm();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to update payment method.');
    } finally {
      setSaving(false);
    }
  };

  const saveNetTerms = async () => {
    const days = parseInt(netTerms, 10);
    if (!Number.isInteger(days) || days < 0 || days > 180) {
      return cogoToast.error('Enter a whole number of days between 0 and 180.');
    }
    setSavingNetTerms(true);
    try {
      await setSpotlightNetTerms(brand.Brand_id, days);
      setNetTermsSaved(days);
      cogoToast.success(days > 0 ? `Net terms set to ${days} day(s).` : 'Net terms removed.');
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to update net terms.');
    } finally {
      setSavingNetTerms(false);
    }
  };

  const toggleSkipPm = async () => {
    const next = !skipPm;
    setSkipPm(next);
    setSavingSkipPm(true);
    try {
      await setSpotlightSkipPaymentMethod(brand.Brand_id, next);
      cogoToast.success(next ? 'Brand can now onboard without a payment method.' : 'Payment method now required.');
    } catch (e) {
      setSkipPm(!next); // revert on failure
      cogoToast.error(typeof e === 'string' ? e : 'Failed to update manual payment setting.');
    } finally {
      setSavingSkipPm(false);
    }
  };

  const saveGrouping = async value => {
    if (value === grouping || savingGrouping) return;
    const previous = grouping;
    setGrouping(value);
    setSavingGrouping(true);
    try {
      await setInvoiceGrouping(brand.Brand_id, value);
      // The detail remounts from the table's row on re-expand, so the row must learn the new value.
      onBrandPatch && onBrandPatch(brand.Brand_id, { invoiceGrouping: value });
      cogoToast.success(
        value === 'per_group' ? 'Scheduled billing now cuts one invoice per set.' : 'Scheduled billing now cuts a single combined invoice.'
      );
    } catch (e) {
      setGrouping(previous);
      cogoToast.error(typeof e === 'string' ? e : 'Failed to update invoice grouping.');
    } finally {
      setSavingGrouping(false);
    }
  };

  const onCancelCredit = async credit => {
    const confirmed = window.confirm(`Cancel the ${fmtMoney2(credit.remaining)} unapplied remainder of "${credit.description}"?`);
    if (!confirmed) return;
    try {
      await cancelBillingCredit(brand.Brand_id, credit.id);
      cogoToast.success('Credit cancelled.');
      loadCredits();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to cancel the credit.');
    }
  };

  if (loading) return <SkeletonLines count={5} />;

  const pmLabel = m => `${m.card ? `${m.card.brand} ····${m.card.last4} (${m.card.exp_month}/${m.card.exp_year})` : m.type} — ${m.id}`;

  return (
    <div className='sl-billing-detail'>
      <section className='sl-billing-card'>
        <div className='sl-billing-header'>
          <div>
            <h3>Invoice history</h3>
            <p>{brand.invoiceExempt ? 'New customer invoices are disabled.' : 'Customer ad-spend invoices.'}</p>
          </div>
          <div className='sl-billing-actions'>
            <button className='sl-btn secondary small' onClick={() => setScheduledOpen(true)}>
              Generate scheduled invoice
            </button>
            <button className='sl-btn secondary small' onClick={() => setSupplementalOpen(true)}>
              Send supplemental invoice
            </button>
            <SpotlightInvoiceExemption brand={brand} onBrandPatch={onBrandPatch} onRefresh={onRefresh} />
          </div>
        </div>
        {(brand.invoiceStartDate || brand.invoiceEligibilityReason) && (
          <div className='sl-billing-status'>
            {brand.invoiceStartDate && <div>Spend before {brand.invoiceStartDate} stays excluded.</div>}
            {brand.invoiceEligibilityReason && <div>Reason: {brand.invoiceEligibilityReason}</div>}
          </div>
        )}
        {supplementalOpen && <SupplementalInvoiceModal brand={brand} onClose={() => setSupplementalOpen(false)} onDone={loadInvoices} />}
        {scheduledOpen && (
          <ScheduledInvoiceModal
            brand={brand}
            onClose={() => setScheduledOpen(false)}
            onDone={() => {
              loadInvoices();
              loadCredits();
            }}
          />
        )}
        <div className='sl-billing-table'>
          <table className='sl-table'>
            <thead>
              <tr>
                <th className='no-sort'>Period</th>
                <th className='no-sort num'>Amount</th>
                <th className='no-sort num'>Spend</th>
                <th className='no-sort num'>Platform fee</th>
                <th className='no-sort num'>Credit</th>
                <th className='no-sort'>Status</th>
                <th className='no-sort'>Stripe</th>
              </tr>
            </thead>
            <tbody>
              {(invoices || []).map(inv => (
                <tr key={inv.id}>
                  <td>
                    {moment(inv.periodStart).format('MMM D')} – {moment(inv.periodEnd).format('MMM D, YYYY')}
                    {inv.billingCadence === 'supplemental' && (
                      <span className='sl-pill blue' style={{ marginLeft: 6 }}>
                        supplemental
                      </span>
                    )}
                    {inv.groupName && (
                      <span className='sl-pill' style={{ marginLeft: 6 }}>
                        {inv.groupName}
                      </span>
                    )}
                  </td>
                  <td className='num'>{fmtMoney2(inv.amount)}</td>
                  <td className='num'>{fmtMoney2(inv.spendAmount)}</td>
                  <td className='num'>{fmtMoney2(inv.platformFeeAmount)}</td>
                  <td className='num'>{inv.creditAmount > 0 ? `-${fmtMoney2(inv.creditAmount)}` : '—'}</td>
                  <td>
                    <span
                      className={cn('sl-pill', { green: inv.status === 'paid', red: inv.status === 'unpaid', amber: inv.status === 'cancelled' })}
                    >
                      {inv.status}
                      {inv.status === 'unpaid' && inv.suppressOverdue ? ' (manual)' : ''}
                    </span>
                  </td>
                  <td>
                    {inv.stripeHostedUrl && (
                      <a href={inv.stripeHostedUrl} target='_blank' rel='noreferrer'>
                        Invoice ↗
                      </a>
                    )}
                    {inv.stripeDashboardUrl && (
                      <>
                        {' '}
                        <a href={inv.stripeDashboardUrl} target='_blank' rel='noreferrer'>
                          Dashboard ↗
                        </a>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {(!invoices || !invoices.length) && (
                <tr>
                  <td colSpan={7} className='sl-empty'>
                    No invoices yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className='sl-billing-card'>
        <div className='sl-billing-header'>
          <div>
            <h3>Payment & invoicing</h3>
            <p>Collection settings for customer ad-spend invoices.</p>
          </div>
          {pm?.stripeCustomerUrl && (
            <a className='sl-billing-link' href={pm.stripeCustomerUrl} target='_blank' rel='noreferrer'>
              Manage in Stripe ↗
            </a>
          )}
        </div>
        <div className='sl-billing-body'>
          <div className='sl-billing-field'>
            <label htmlFor={`spotlight-payment-${brand.Brand_id}`}>Payment method</label>
            <div className='sl-billing-controls'>
              {pm?.paymentMethods?.length ? (
                <>
                  <select id={`spotlight-payment-${brand.Brand_id}`} value={selectedPm} onChange={e => setSelectedPm(e.target.value)}>
                    <option value=''>Select a payment method</option>
                    {pm.paymentMethods.map(m => (
                      <option key={m.id} value={m.id}>
                        {pmLabel(m)}
                      </option>
                    ))}
                  </select>
                  <button
                    className='sl-btn secondary small'
                    disabled={saving || !selectedPm || selectedPm === pm.currentPaymentMethodId}
                    onClick={savePm}
                  >
                    {saving ? 'Saving…' : 'Save method'}
                  </button>
                </>
              ) : (
                <span className='sl-billing-muted'>
                  {pm === null ? 'Loading payment methods…' : pm === false ? 'Could not load payment methods.' : 'No payment methods on file.'}
                </span>
              )}
            </div>
          </div>
          <div className='sl-billing-field'>
            <label htmlFor={`spotlight-net-terms-${brand.Brand_id}`}>Net terms</label>
            <div className='sl-billing-controls'>
              <input
                id={`spotlight-net-terms-${brand.Brand_id}`}
                type='number'
                min={0}
                max={180}
                step={1}
                value={netTerms}
                onChange={e => setNetTerms(e.target.value)}
              />
              <span className='sl-billing-muted'>days · 0 = auto-charge</span>
              <button className='sl-btn secondary small' disabled={savingNetTerms || parseInt(netTerms, 10) === netTermsSaved} onClick={saveNetTerms}>
                {savingNetTerms ? 'Saving…' : 'Save terms'}
              </button>
            </div>
          </div>
          <div className='sl-billing-field'>
            <span>Manual payment</span>
            <label className='sl-billing-checkbox'>
              <input type='checkbox' checked={skipPm} disabled={savingSkipPm} onChange={toggleSkipPm} />
              Allow onboarding without a payment method
            </label>
          </div>
          <div className='sl-billing-field'>
            <span>Invoice grouping</span>
            <div className='sl-billing-grouping' role='group' aria-label='Invoice grouping'>
              <button aria-pressed={grouping === 'combined'} disabled={savingGrouping} onClick={() => saveGrouping('combined')}>
                Single invoice
              </button>
              <button aria-pressed={grouping === 'per_group'} disabled={savingGrouping} onClick={() => saveGrouping('per_group')}>
                Invoice per set
              </button>
            </div>
          </div>
          <details className='sl-billing-help'>
            <summary>How these settings affect billing</summary>
            <p>Net terms send the invoice for manual payment, due that many days out, without auto-pausing ads or showing overdue notices.</p>
            <p>
              Manual payment allows onboarding without a card. Without a card, invoices are due immediately and can show as overdue. Adding a card
              enables auto-charge when net terms are zero.
            </p>
            <p>
              A single invoice combines spend from every set. Invoice per set creates a separate scheduled invoice for each set. These settings do not
              change affiliate or subscription invoices.
            </p>
          </details>
        </div>
      </section>

      <section className='sl-billing-card'>
        <div className='sl-billing-header'>
          <div>
            <h3>Invoice credits</h3>
            <p>Applied automatically to upcoming scheduled invoices.</p>
          </div>
          <button className='sl-btn secondary small' onClick={() => setCreditOpen(true)}>
            Add credit
          </button>
        </div>
        {creditOpen && <BillingCreditModal brand={brand} onClose={() => setCreditOpen(false)} onDone={loadCredits} />}
        {credits && credits.length > 0 ? (
          <div className='sl-billing-table'>
            <table className='sl-table'>
              <thead>
                <tr>
                  <th className='no-sort'>Description</th>
                  <th className='no-sort num'>Amount</th>
                  <th className='no-sort num'>Applied</th>
                  <th className='no-sort num'>Remaining</th>
                  <th className='no-sort'>Status</th>
                  <th className='no-sort'>Added</th>
                  <th className='no-sort' style={{ width: 80 }} />
                </tr>
              </thead>
              <tbody>
                {credits.map(credit => (
                  <tr key={credit.id}>
                    <td>
                      {credit.description}
                      {credit.internalNote && <div style={{ color: '#9a9a9a', fontSize: 12 }}>{credit.internalNote}</div>}
                    </td>
                    <td className='num'>{fmtMoney2(credit.amount)}</td>
                    <td className='num'>{credit.amountApplied > 0 ? fmtMoney2(credit.amountApplied) : '—'}</td>
                    <td className='num'>{credit.status === 'pending' ? fmtMoney2(credit.remaining) : '—'}</td>
                    <td>
                      <span
                        className={cn('sl-pill', {
                          green: credit.status === 'applied',
                          blue: credit.status === 'pending',
                          amber: credit.status === 'cancelled'
                        })}
                      >
                        {credit.status}
                      </span>
                    </td>
                    <td>
                      {moment(credit.createdAt).format('MMM D, YYYY')}
                      {credit.createdByName && <span style={{ color: '#9a9a9a' }}> · {credit.createdByName}</span>}
                    </td>
                    <td>
                      {credit.status === 'pending' && (
                        <button className='sl-btn danger small' onClick={() => onCancelCredit(credit)}>
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className='sl-billing-empty'>No credits. Pending credits are deducted from the next scheduled invoice.</div>
        )}
      </section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Trials
// ---------------------------------------------------------------------------
const TrialsTable = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [grantOpen, setGrantOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getTrials();
      setData(r);
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to load trials.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onCancel = async brandId => {
    try {
      const r = await cancelTrial(brandId);
      if (r.result?.status === 'cancelled') cogoToast.success('Trial cancelled.');
      else cogoToast.warn(`Could not cancel: ${r.result?.status}`);
      load();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to cancel.');
    }
  };

  const onEdit = async brandId => {
    const input = window.prompt('New trial length in days from now:');
    if (!input) return;
    const d = parseInt(input, 10);
    if (!d || d <= 0) return cogoToast.error('Enter a positive number of days.');
    try {
      await editTrial(brandId, d);
      cogoToast.success('Trial updated.');
      load();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to edit.');
    }
  };

  if (loading && !data) return <SkeletonLines count={6} />;
  if (error) return <div className='sl-error'>{error}</div>;

  const noOffer = data && !data.offer;

  return (
    <div>
      <div className='sl-controls' style={{ justifyContent: 'space-between' }}>
        <span style={{ color: '#767676', fontSize: 13 }}>{(data?.trials || []).length} active trial(s)</span>
        {!noOffer && (
          <button className='sl-btn' onClick={() => setGrantOpen(true)}>
            Grant trial
          </button>
        )}
      </div>

      {noOffer && (
        <div className='sl-section'>
          <div className='sl-empty' style={{ textAlign: 'left', padding: '4px 0' }}>
            No <strong>SPOTLIGHTS</strong> subscription offer is configured, so trials can't be granted yet. Add the SPOTLIGHTS feature to a
            SubscriptionOffer (e.g.{' '}
            <code>UPDATE SubscriptionOffers SET canAccessEnum = CONCAT_WS(',', canAccessEnum, 'SPOTLIGHTS') WHERE id = &lt;id&gt;</code>) and reload.
          </div>
        </div>
      )}

      {grantOpen && <TrialGrantModal onClose={() => setGrantOpen(false)} onDone={load} />}

      {/* Active trials */}
      <div className='sl-card-label'>Active trials</div>
      <table className='sl-table' style={{ marginTop: 6 }}>
        <thead>
          <tr>
            <th className='no-sort'>Brand</th>
            <th className='no-sort'>Started</th>
            <th className='no-sort'>Ends</th>
            <th className='no-sort num'>Days left</th>
            <th className='no-sort num'>Length</th>
            <th className='no-sort'>Spotlights</th>
            <th className='no-sort'>Actions</th>
          </tr>
        </thead>
        <tbody>
          {(data?.trials || []).map(t => (
            <tr key={t.Brand_id}>
              <td>
                <div className='sl-brand-cell'>
                  {t.brandLogo && <img src={getSmartImage(t.brandLogo)} alt='' />}
                  {t.brandName} <span style={{ color: '#9a9a9a' }}>#{t.Brand_id}</span>
                </div>
              </td>
              <td>{t.startedAt ? moment(t.startedAt).format('MMM D, YYYY') : '—'}</td>
              <td>
                {t.trialEndsAt ? moment(t.trialEndsAt).format('MMM D, YYYY') : '—'}
                {!t.isActive && (
                  <span className='sl-pill red' style={{ marginLeft: 6 }}>
                    expired
                  </span>
                )}
              </td>
              <td className='num'>{t.daysRemaining != null ? t.daysRemaining : '—'}</td>
              <td className='num'>{t.trialLengthDays != null ? `${t.trialLengthDays}d` : '—'}</td>
              <td>{t.isSpotlightEnabled ? <span className='sl-pill green'>enabled</span> : <span className='sl-pill'>off</span>}</td>
              <td>
                <button className='sl-btn secondary small' onClick={() => onEdit(t.Brand_id)} disabled={t.isLiveOnStripe}>
                  Edit
                </button>{' '}
                <button className='sl-btn danger small' onClick={() => onCancel(t.Brand_id)} disabled={t.isLiveOnStripe}>
                  Cancel
                </button>
                {t.isLiveOnStripe && (
                  <span className='sl-pill amber' style={{ marginLeft: 6 }}>
                    live on Stripe
                  </span>
                )}
              </td>
            </tr>
          ))}
          {(!data?.trials || !data.trials.length) && (
            <tr>
              <td colSpan={7} className='sl-empty'>
                No active trials.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SpotlightsBilling;
